/**
 * SuggestionDetailModal — modal que se abre cuando la marca clickea una
 * sugerencia de Deenex (origin === 'suggested', status === 'suggested').
 *
 * 3 acciones disponibles:
 *   - Aceptar tal cual    → status passa a 'active' inmediatamente
 *   - Editar y aceptar    → convierte a custom + draft (para tunear copy)
 *   - Descartar           → pide motivo + status passa a 'dismissed'
 *
 * El motivo de descarte es un set de chips rápidos (no es campo libre).
 */

import { useEffect, useState } from "react";
import {
  X,
  Sparkles,
  Check,
  Pencil,
  Trash2,
  Loader2,
  Clock,
  MessageSquare,
  Users,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import type { Objective } from "../types";

interface SuggestionDetailModalProps {
  campaign: Objective;
  busy?: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDismiss: (reason: string) => void;
  onEdit: (updates: { name?: string; messagePreview?: string }) => void;
}

type Mode = "view" | "edit" | "dismiss";

const DISMISS_REASONS = [
  "No es prioridad ahora",
  "No me convence el copy",
  "El segmento no es el correcto",
  "Otro momento",
  "Otro motivo",
];

export default function SuggestionDetailModal({
  campaign,
  busy = false,
  onClose,
  onAccept,
  onDismiss,
  onEdit,
}: SuggestionDetailModalProps) {
  const [mode, setMode] = useState<Mode>("view");
  const [editedName, setEditedName] = useState(campaign.name);
  const [editedMessage, setEditedMessage] = useState(
    campaign.messagePreview || "",
  );
  const [selectedReason, setSelectedReason] = useState<string>("");

  // ESC para cerrar + body scroll lock
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleConfirmEdit = () => {
    onEdit({
      name: editedName !== campaign.name ? editedName : undefined,
      messagePreview:
        editedMessage !== campaign.messagePreview ? editedMessage : undefined,
    });
  };

  const handleConfirmDismiss = () => {
    if (!selectedReason) return;
    onDismiss(selectedReason);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto animate-zoom-in"
          role="dialog"
          aria-modal="true"
        >
          {/* Header con gradient violeta */}
          <div className="relative px-6 py-5 bg-gradient-to-br from-violet-600 via-fuchsia-600 to-purple-600 text-white overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl flex-shrink-0">
                  {campaign.emoji}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Sparkles size={11} />
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">
                      Sugerencia de Deenex
                    </span>
                  </div>
                  <h2 className="text-xl font-bold leading-tight truncate">
                    {campaign.name}
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Body scrollable */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {mode === "view" && (
              <>
                {/* Por qué te la sugiero */}
                {campaign.suggestionReason && (
                  <section>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                      <Sparkles size={11} className="text-violet-500" />
                      Por qué te la sugiero
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed bg-violet-50/50 border border-violet-100 rounded-xl p-4">
                      {campaign.suggestionReason}
                    </p>
                  </section>
                )}

                {/* Preview del mensaje */}
                {campaign.messagePreview && (
                  <section>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                      <MessageSquare size={11} />
                      Mensaje que se va a enviar
                    </h3>
                    <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4">
                      <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
                        {campaign.messagePreview}
                      </p>
                    </div>
                  </section>
                )}

                {/* Meta info */}
                <section className="grid grid-cols-2 gap-3">
                  {campaign.suggestionExpiresAt && (
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                        <Clock size={10} />
                        Mejor activar antes del
                      </p>
                      <p className="text-sm font-semibold text-slate-800">
                        {new Date(
                          campaign.suggestionExpiresAt,
                        ).toLocaleDateString("es-AR", {
                          day: "numeric",
                          month: "long",
                        })}
                      </p>
                    </div>
                  )}
                  {campaign.segmentId && (
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                        <Users size={10} />
                        Segmento sugerido
                      </p>
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        Configurado por Deenex
                      </p>
                    </div>
                  )}
                </section>

                {/* IA validation note */}
                <section className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <p className="text-xs text-blue-700 leading-relaxed">
                    💡 <span className="font-semibold">Ya validé esto:</span>{" "}
                    tono compatible con tu manual, no choca con campañas
                    activas, respeta tu ventana horaria y frecuencia.
                  </p>
                </section>
              </>
            )}

            {mode === "edit" && (
              <>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Editar antes de activar
                </h3>

                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                    Nombre de la campaña
                  </label>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                    Mensaje a enviar
                  </label>
                  <textarea
                    value={editedMessage}
                    onChange={(e) => setEditedMessage(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 resize-none"
                  />
                  <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                    Cuando confirmes, esta campaña pasa a la pila de "Mías" y
                    Deenex la revisa antes de activar.
                  </p>
                </div>
              </>
            )}

            {mode === "dismiss" && (
              <>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  ¿Por qué descartás esta sugerencia?
                </h3>
                <p className="text-sm text-slate-600">
                  Lo uso para aprender qué proponerte mejor la próxima.
                </p>
                <div className="space-y-2">
                  {DISMISS_REASONS.map((r) => (
                    <button
                      key={r}
                      onClick={() => setSelectedReason(r)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                        selectedReason === r
                          ? "bg-violet-50 border-violet-300 text-violet-900"
                          : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer con acciones según mode */}
          <div className="border-t border-slate-100 p-4 flex items-center justify-between gap-2 bg-slate-50/50">
            {mode === "view" && (
              <>
                <button
                  onClick={() => setMode("dismiss")}
                  className="text-sm font-medium text-slate-500 hover:text-red-600 px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors"
                  disabled={busy}
                >
                  <Trash2 size={14} />
                  Descartar
                </button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setMode("edit")}
                    disabled={busy}
                  >
                    <Pencil size={14} />
                    Editar antes
                  </Button>
                  <Button
                    variant="primary"
                    onClick={onAccept}
                    disabled={busy}
                  >
                    {busy ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} />
                    )}
                    Aceptar tal cual
                  </Button>
                </div>
              </>
            )}

            {mode === "edit" && (
              <>
                <button
                  onClick={() => setMode("view")}
                  className="text-sm font-medium text-slate-500 hover:text-slate-800 px-3 py-2 rounded-lg"
                >
                  ← Volver
                </button>
                <Button
                  variant="primary"
                  onClick={handleConfirmEdit}
                  disabled={busy || !editedMessage.trim()}
                >
                  {busy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Pencil size={14} />
                  )}
                  Guardar como borrador
                </Button>
              </>
            )}

            {mode === "dismiss" && (
              <>
                <button
                  onClick={() => setMode("view")}
                  className="text-sm font-medium text-slate-500 hover:text-slate-800 px-3 py-2 rounded-lg"
                >
                  ← Volver
                </button>
                <Button
                  variant="primary"
                  onClick={handleConfirmDismiss}
                  disabled={busy || !selectedReason}
                >
                  {busy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  Confirmar descarte
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
